<script setup lang="ts">
import {ref} from 'vue'
import {useLocalization} from '@hantera/portal-app'
import {useClose} from "@hantera/design-system";

const props = defineProps<{
  password: string
  userName: string
  userEmail: string
}>()

const t = useLocalization('iam.principals')

const passwordCopied = ref(false)
const close = useClose()

async function copyPassword() {
  try {
    await navigator.clipboard.writeText(props.password)
    passwordCopied.value = true
  } catch (error) {
    // Fallback for iframe restrictions
    const textArea = document.createElement('textarea')
    textArea.value = props.password
    textArea.style.position = 'fixed'
    textArea.style.left = '-999999px'
    document.body.appendChild(textArea)
    textArea.select()
    try {
      document.execCommand('copy')
      passwordCopied.value = true
    } catch (err) {
      console.error('Failed to copy password:', err)
    }
    document.body.removeChild(textArea)
  }
}

function handleClose() {
  if (!passwordCopied.value) {
    const confirmed = confirm(t('create.passwordDisplay.closeWarning'))
    if (!confirmed) return
  }
  close()
}
</script>

<template>
  <div class="password-display p2">
    <div class="box-warning p2">
      {{ t('create.passwordDisplay.warning') }}
    </div>

    <dl class="box-content">
      <dt>{{ t('profile.name') }}</dt>
      <dd>{{ userName }}</dd>
      <dt>{{ t('profile.email') }}</dt>
      <dd>{{ userEmail }}</dd>
      <dt>{{ t('create.passwordDisplay.password') }}</dt>
      <dd><code class="password-text">{{ password }}</code></dd>
    </dl>

    <div class="copy-actions">
      <button @click="copyPassword" class="btn-primary">
        <span v-if="!passwordCopied">📋 {{ t('create.passwordDisplay.copy') }}</span>
        <span v-else>✓ {{ t('create.passwordDisplay.copied') }}</span>
      </button>
    </div>

    <p>
      {{ t('create.passwordDisplay.instructions') }}
    </p>

    <div class="dialog-actions">
      <button @click="handleClose" class="btn-secondary">
        {{ t('create.passwordDisplay.close') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.password-display {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 450px;
  
}

.password-box {
  border: 2px solid var(--color-primary);
  border-radius: 6px;
  padding: 1.5rem;
  text-align: center;
}

.password-text {
  font-size: 1.25rem;
  font-family: 'Courier New', monospace;
  letter-spacing: 2px;
  word-break: break-all;
  font-weight: 500;
}

.copy-actions {
  display: flex;
  justify-content: center;
}

.instruction-text {
  font-size: 0.875rem;
  text-align: center;
  margin: 0;
}

.dialog-actions {
  display: flex;
  justify-content: center;
  padding-top: 1rem;
}
</style>
